import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import { useQueryClient } from "@tanstack/react-query";
import {
  storyFields,
  storyLabels,
  storyValues,
  storyValuesSchema,
  storyWorkspaceSchema,
  type CuratorArtwork,
  type StoryField,
  type StoryRun,
  type StoryValues
} from "@eaf/contracts";
import { useAuth } from "../auth/auth-context";
import { readSessionDraft, writeSessionDraft } from "../lib/session-draft";
import { StoryHistory } from "./story-history";
import { useLiveHistory, liveHistoriesSchema } from "../lib/live-history";
import { LiveHistoryControls } from "./live-history-controls";

const recoverySchema = z.object({
  values: storyValuesSchema,
  saved: storyValuesSchema,
  watched: z.record(z.string(), storyValuesSchema),
  handled: z.array(z.string()),
  histories: liveHistoriesSchema.optional()
});

export function StorytellingEditor({ record }: { record: CuratorArtwork }) {
  const auth = useAuth();
  const authRef = useRef(auth);
  authRef.current = auth;
  const cache = useQueryClient();
  const recoveryKey = `eaf:story-draft:${auth.currentUser?.userId}:${record.artworkId}`;
  const [recovery] = useState(() => readSessionDraft(recoveryKey, recoverySchema));
  const [initial] = useState(() => {
    const server = storyValues(record.document);
    const values = { ...server },
      saved = { ...server };
    for (const key of storyFields) {
      if (
        recovery &&
        recovery.values[key] !== recovery.saved[key] &&
        recovery.values[key] !== server[key]
      ) {
        values[key] = recovery.values[key];
        saved[key] = recovery.saved[key];
      }
    }
    return { values, saved };
  });
  const history = useLiveHistory(initial.values, recovery?.histories);
  const { values, valuesRef } = history;
  const [saved, setSaved] = useState(initial.saved);
  const [recoveryAvailable, setRecoveryAvailable] = useState(true);
  const [runs, setRuns] = useState<StoryRun[]>([]);
  const [error, setError] = useState("");
  const [connection, setConnection] = useState("");
  const [saving, setSaving] = useState(false);
  const [requesting, setRequesting] = useState<StoryField[]>([]);
  const [now, setNow] = useState(Date.now());
  const seenActive = useRef(new Map<string, StoryValues>(Object.entries(recovery?.watched ?? {})));
  const handled = useRef(new Set<string>(recovery?.handled ?? []));
  const startWatch = useRef<() => void>(() => undefined);
  const path = `/curator/artworks/${record.artworkId}/storytelling`;
  const canRun = auth.currentUser?.permissions.includes("ai.run") ?? false;
  const canSave = auth.currentUser?.permissions.includes("artwork.update") ?? false;
  useEffect(() => {
    const latest = storyValues(record.document);
    const clean = Object.fromEntries(
      storyFields.flatMap((key) =>
        latest[key] !== saved[key] &&
        (valuesRef.current[key] === saved[key] || valuesRef.current[key] === latest[key])
          ? [[key, latest[key]]]
          : []
      )
    );
    if (Object.keys(clean).length) {
      history.reset(clean);
      setSaved((current) => ({ ...current, ...clean }));
    }
    // Dirty fields retain their original expected value for conflict detection.
  }, [record.document]);
  const activeFor = (key: StoryField) =>
    runs.find((run) => ["pending", "running"].includes(run.state) && run.fields.includes(key));
  const busy = runs.some((run) => ["pending", "running"].includes(run.state));
  useEffect(() => {
    if (
      !writeSessionDraft(recoveryKey, {
        values,
        saved,
        histories: history.histories,
        watched: Object.fromEntries(seenActive.current),
        handled: [...handled.current]
      })
    )
      setRecoveryAvailable(false);
  }, [recoveryKey, values, saved, runs, requesting, history.histories]);

  useEffect(() => {
    const controller = new AbortController();
    let watching = false;
    let requested = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let stopped = false;
    const accept = (value: unknown) => {
      const snapshot = storyWorkspaceSchema.parse(value);
      setRuns(snapshot.runs);
      for (const run of snapshot.runs) {
        if (["pending", "running"].includes(run.state) && !seenActive.current.has(run.id))
          seenActive.current.set(run.id, { ...valuesRef.current });
        if (
          run.state === "succeeded" &&
          run.result &&
          seenActive.current.has(run.id) &&
          !handled.current.has(run.id)
        ) {
          handled.current.add(run.id);
          const baseline = seenActive.current.get(run.id)!;
          for (const key of run.fields)
            if (valuesRef.current[key] === baseline[key] && typeof run.result[key] === "string")
              history.change(key, run.result[key]);
        }
      }
      const activeIds = new Set(
        snapshot.runs
          .filter((run) => ["pending", "running"].includes(run.state))
          .map((run) => run.id)
      );
      for (const id of seenActive.current.keys())
        if (!activeIds.has(id)) seenActive.current.delete(id);
      const recentIds = new Set(snapshot.runs.map((run) => run.id));
      for (const id of handled.current) if (!recentIds.has(id)) handled.current.delete(id);
      return snapshot.runs.some((run) => ["pending", "running"].includes(run.state));
    };
    const watch = async () => {
      if (stopped) return;
      if (watching) {
        requested = true;
        return;
      }
      watching = true;
      try {
        let running = accept(
          await authRef.current.authorizedFetch(path, { signal: controller.signal })
        );
        while (running && !stopped) {
          try {
            setConnection("");
            await authRef.current.authorizedStream(
              `${path}/events`,
              (value) => {
                running = accept(value);
              },
              controller.signal
            );
          } catch {
            if (!stopped) setConnection("Reconnecting to your saved analysis…");
          }
          if (running && !stopped) {
            await new Promise((resolve) => setTimeout(resolve, 2000));
            if (!stopped)
              running = accept(
                await authRef.current.authorizedFetch(path, { signal: controller.signal })
              );
          }
        }
      } catch (cause) {
        if (!stopped) {
          setError(cause instanceof Error ? cause.message : "Could not load saved analysis.");
          retryTimer = setTimeout(() => void watch(), 3000);
        }
      } finally {
        watching = false;
        if (requested && !stopped) {
          requested = false;
          void watch();
        }
      }
    };
    startWatch.current = () => {
      void watch();
    };
    void watch();
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => {
      stopped = true;
      controller.abort();
      clearInterval(timer);
      clearTimeout(retryTimer);
    };
  }, [path]);

  const generate = async (fields: StoryField[]) => {
    if (
      fields.some((key) => values[key] !== saved[key]) &&
      !window.confirm("Replace unsaved edits in the selected descriptions with new AI suggestions?")
    )
      return;
    setError("");
    setRequesting(fields);
    try {
      const result = await auth.authorizedFetch<{ aiRunId: string }>(path, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ fields, context: values })
      });
      seenActive.current.set(result.aiRunId, { ...valuesRef.current });
      startWatch.current();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Analysis could not start.");
    } finally {
      setRequesting([]);
    }
  };
  const save = async (fields: StoryField[]) => {
    const changed = fields.filter((key) => values[key] !== saved[key]);
    if (!changed.length) return;
    const patch = Object.fromEntries(changed.map((key) => [key, values[key]]));
    setError("");
    setSaving(true);
    try {
      await auth.authorizedFetch(path, {
        method: "PATCH",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ patch, expected: saved })
      });
      setSaved((current) => ({ ...current, ...patch }));
      history.reset(patch);
      await cache.invalidateQueries({ queryKey: ["curator-artwork", record.artworkId] });
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Descriptions could not be saved.");
    } finally {
      setSaving(false);
    }
  };
  const restore = (key: StoryField, run: StoryRun) => {
    if (
      values[key] !== saved[key] &&
      !window.confirm("Replace this field's unsaved edits with the saved AI suggestion?")
    )
      return;
    history.change(key, run.result?.[key] ?? values[key]);
  };
  return (
    <div>
      <Button
        className="mb-4"
        disabled={!canRun || busy || requesting.length > 0 || saving}
        onClick={() => void generate([...storyFields])}
      >
        Run Analysis on All
      </Button>
      {(busy || requesting.length > 0) && (
        <div role="status" aria-live="polite" className="mb-4">
          {busy ? (
            runs
              .filter((run) => ["pending", "running"].includes(run.state))
              .map((run) => (
                <p key={run.id}>
                  <Spinner as="span" size="sm" className="me-2" />
                  {run.fields.length === 4
                    ? "All descriptions"
                    : run.fields.map((key) => storyLabels[key]).join(", ")}
                  : {run.progress}
                  {` — ${Math.max(0, Math.floor((now - Date.parse(run.createdAt)) / 1000))} seconds elapsed`}
                </p>
              ))
          ) : (
            <p>
              <Spinner as="span" size="sm" className="me-2" />
              Preparing your analysis…
            </p>
          )}
        </div>
      )}
      {!recoveryAvailable && (
        <Alert variant="warning">
          This browser could not keep a recovery draft. Save your edits before leaving this page.
        </Alert>
      )}
      {error && <Alert variant="danger">{error}</Alert>}
      {connection && <p role="status">{connection}</p>}
      {storyFields.map((key) => {
        const active = activeFor(key);
        const pending = requesting.includes(key) || Boolean(active);
        const last = runs.find(
          (run) => run.state === "succeeded" && typeof run.result?.[key] === "string"
        );
        const failed = runs.find((run) => run.fields.includes(key))?.error;
        return (
          <Form.Group key={key} controlId={`story-${key}`} className="mb-4">
            <div className="d-flex justify-content-between gap-2 mb-2">
              <Form.Label>{storyLabels[key]}</Form.Label>
              <Badge
                bg={values[key] === saved[key] ? "secondary" : "warning"}
                {...(values[key] === saved[key] ? {} : { text: "dark" as const })}
              >
                {values[key] === saved[key] ? "Saved" : "Unsaved changes"}
              </Badge>
            </div>
            <Form.Control
              as="textarea"
              maxLength={6000}
              rows={key === "aCloserLook" ? 12 : 10}
              readOnly={pending || saving || !canSave}
              value={active?.preview[key] ?? values[key]}
              onChange={(event) => history.change(key, event.target.value, true)}
              onBlur={() => history.finish(key)}
            />
            {key === "fromTheCollection" && !pending && !values[key].trim() && (
              <Form.Text className="text-secondary">
                Add a memory or known background about this artwork. AI can help polish your notes.
              </Form.Text>
            )}
            {!pending && failed && (
              <p role="alert" className="mt-2">
                {failed}
              </p>
            )}
            <div className="d-flex flex-wrap gap-2 mt-2">
              <LiveHistoryControls
                label={storyLabels[key]}
                history={history.histories[key]}
                disabled={pending || saving || !canSave}
                onMove={(step) => history.move(key, step)}
              />
              <Button
                variant="outline-light"
                aria-label={`Rerun AI for ${storyLabels[key]}`}
                disabled={!canRun || pending || saving || requesting.length > 0}
                onClick={() => void generate([key])}
              >
                Rerun AI<span className="visually-hidden"> for {storyLabels[key]}</span>
              </Button>
              <Button
                aria-label={`Save ${storyLabels[key]}`}
                disabled={!canSave || pending || saving || values[key] === saved[key]}
                onClick={() => void save([key])}
              >
                Save<span className="visually-hidden"> {storyLabels[key]}</span>
              </Button>
              <StoryHistory
                artworkId={record.artworkId}
                field={key}
                disabled={pending || saving || !canSave}
                onUse={(text) => history.change(key, text)}
              />
              {last && last.result?.[key] !== values[key] && (
                <Button
                  variant="link"
                  disabled={pending || saving || !canSave}
                  onClick={() => void restore(key, last)}
                >
                  Use last suggestion
                  <span className="visually-hidden"> for {storyLabels[key]}</span>
                </Button>
              )}
            </div>
          </Form.Group>
        );
      })}
      <Button
        disabled={
          !canSave ||
          busy ||
          requesting.length > 0 ||
          saving ||
          storyFields.every((key) => values[key] === saved[key])
        }
        onClick={() => void save([...storyFields])}
      >
        {saving ? "Saving…" : "Save All"}
      </Button>
    </div>
  );
}
